import React, { useEffect, useState } from 'react'
import { useQuery } from "@apollo/client/react";
import { Button } from '@/atoms/Button'
import { CircleCheckIcon } from '@/atoms/Icons/CircleCheckIcon'
import { EyebrowSM, ParagraphSM } from '@/atoms/Text'
import { CollectibleCartItem } from '@/contexts/CollectiblesPurchaseContext/CollectiblesPurchaseContextProvider'
import { GET_ADDRESS } from '@/services/NftService'
import { UserData } from '@/services/NftService/types'
import { getObjectFromLocalStorage } from '@/utils/local-storage/local-storage'
import { COLLECTIBLE_SHOP_CART } from '@/utils/local-storage/local-storage-keys'
import { logger } from '@/utils/logging'
import { useTranslate } from '@/utils/translate/translate-client'
import AddressChange from '../AddressChange'
import { AddressText } from './AddressText'

interface AddressSectionProps {
  isAddressState: boolean
  changeAddressCheckoutState: () => void
  changePaymentCheckoutState: () => void
  handleUpdatePaymentIntentAmount: () => void
  orderId?: string
}

const AddressSection: React.FC<AddressSectionProps> = ({
  isAddressState,
  changeAddressCheckoutState,
  changePaymentCheckoutState,
  handleUpdatePaymentIntentAmount,
  orderId,
}) => {
  const [isAddressChange, setIsAddressChange] = useState(false)
  const { t } = useTranslate('account')

  const [needsShippingAddress, setNeedsShippingAddress] = useState(false)
  const [shippingAddress, setShippingAddress] = useState({
    line1: '',
    line2: '',
    city: '',
    zip: '',
    state: '',
    country: '',
  })
  useEffect(() => {
    const cart = getObjectFromLocalStorage<CollectibleCartItem[]>(COLLECTIBLE_SHOP_CART, [])
    const hasKeepsake = cart.reduce(
      (accum, v) =>
        accum
          ? true
          : v?.products?.reduce((accum, p) => (accum ? true : p?.__typename == 'CollectibleProductKeepsake'), false),
      false,
    )
    setNeedsShippingAddress(hasKeepsake)
  }, [])

  const { data, loading, refetch, error: addressError } = useQuery<UserData>(GET_ADDRESS, {
    errorPolicy: 'all',
  })

  useEffect(() => {
    if (data) {
      if (data?.user?.isAddressValid) {
        changePaymentCheckoutState()
        return
      }
      setIsAddressChange(true)
    }
  }, [data, changePaymentCheckoutState])

  useEffect(() => {
    if (addressError) {
      logger().error('An error occurred while attempting to fetch the user address', addressError)
    }
  }, [addressError])

  const cancelAddressChange = () => {
    setIsAddressChange(false)
    changePaymentCheckoutState()
  }

  const successAddressChange = () => {
    refetch()
    handleUpdatePaymentIntentAmount()
    changePaymentCheckoutState()
    setIsAddressChange(false)
  }

  const handleAddressChange = () => {
    changeAddressCheckoutState()
    setIsAddressChange(true)
  }

  return (
    <>
      <div className="flex flex-row w-full">
        <div className="flex flex-col w-full gap-y-2">
          <EyebrowSM color="core-gray-700">{t('billingAddress', 'Billing Address')}</EyebrowSM>
          <div className="flex w-full">
            {!isAddressChange && (
              <div className="flex flex-row justify-between items-center gap-2 border rounded-2xl border-core-gray-400 pl-3 pr-5 py-4 w-full">
                <div className="mx-auto" key="addressCheckIcon">
                  <CircleCheckIcon
                    size={24}
                    aria-hidden={isAddressState ? 'false' : 'true'}
                    color1="teal"
                    color2="white"
                  />
                </div>
                <AddressText loading={loading} address={data?.user?.address} />

                {!loading && !isAddressState && (
                  <Button
                    variant="plain"
                    className="text-xs text-core-gray-700 justify-self-end"
                    onClick={handleAddressChange}
                  >
                    <ParagraphSM className="text-cool-02 underline">{t('changeAddress', 'change')}</ParagraphSM>
                  </Button>
                )}
              </div>
            )}
            {isAddressChange && (
              <AddressChange
                address={data?.user?.address}
                isAddressChange={isAddressChange}
                hideContinueButton={needsShippingAddress}
                cancelAddressChange={cancelAddressChange}
                successAddressChange={successAddressChange}
              />
            )}
          </div>
        </div>
      </div>
      {needsShippingAddress && (
        <div className="flex flex-row w-full pt-4">
          <div className="flex flex-col w-full gap-y-2">
            <EyebrowSM color="core-gray-700">{t('shippingAddress', 'Shipping Address')}</EyebrowSM>
            <div className="flex w-full">
              {!isAddressChange && (
                <div className="flex flex-row justify-between items-center gap-2 border rounded-2xl border-core-gray-400 pl-3 pr-5 py-4 w-full">
                  <AddressText loading={loading} address={shippingAddress} />
                  {!loading && !isAddressState && (
                    <Button
                      variant="plain"
                      className="text-xs text-core-gray-700 justify-self-end"
                      onClick={handleAddressChange}
                    >
                      <ParagraphSM className="text-cool-02 underline">{t('changeAddress', 'change')}</ParagraphSM>
                    </Button>
                  )}
                </div>
              )}
              {isAddressChange && (
                <AddressChange
                  address={data?.user?.address}
                  shippingAddress={shippingAddress}
                  isAddressChange={isAddressChange}
                  isShippingAddress={needsShippingAddress}
                  cancelAddressChange={cancelAddressChange}
                  successShippingAddressChange={successAddressChange}
                  orderId={orderId}
                  setShippingAddress={setShippingAddress}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export { AddressSection }
